'use client';

import { useTranslations } from 'next-intl';
import React from 'react';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { IconBack16, IconBack24 } from '@cineamo/ui/src/icons/ic-back';

/** Back to the page the guest came from. `iconOnly` is the arrow alone, for the header on small screens. */
const BackButton: React.FC<{ label?: string; iconOnly?: boolean; className?: string }> = ({
    label,
    iconOnly,
    className
}) => {
    const t = useTranslations();

    const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Get tracked navigation history from our custom tracker
        const navigationHistory = sessionStorage.getItem('navigationHistory');

        if (navigationHistory && navigationHistory !== window.location.href) {
            // Clear it so we don't reuse it
            sessionStorage.removeItem('navigationHistory');
            window.location.href = navigationHistory;
            return;
        }

        // Fallback: try document.referrer (for non-SPA navigations)
        if (document.referrer && document.referrer !== window.location.href) {
            window.location.href = document.referrer;
            return;
        }

        // Last fallback: go to home
        window.location.href = '/';
    };

    return (
        <div
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    handleClick(e);
                }
            }}
            className={cn(
                'flex items-center gap-8 text-body-10 hover:opacity-80 transition-opacity cursor-pointer',
                iconOnly && 'p-4 rounded-4 outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-color)]',
                className
            )}
            aria-label={label || t('common.back-to-program')}>
            {iconOnly ? (
                <IconBack24 fill="currentColor" />
            ) : (
                <>
                    <IconBack16 />
                    <span>{label || t('common.back-to-program')}</span>
                </>
            )}
        </div>
    );
};

export default BackButton;
